import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Search, Loader2, MailWarning, X, Globe, Wallet, Clock, Landmark, Sparkles, Printer, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmt, formatDateShort } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import AvocatSheet from "@/components/AvocatSheet";
import { QC_DISTRICTS } from "@/components/avocat/constants";

// Taille de page côté serveur — assez grande pour limiter le nombre d'appels
// mais sans dépasser ce que la BD legacy peut servir rapidement.
const PAGE_SIZE = 50;

// Configuration des filtres "flag" (statuts) — multi-sélection ET (intersection).
const FLAG_FILTERS = [
    { key: "factweb",    label: "Web",        icon: Globe,    color: "#0284C7" },
    { key: "payable",    label: "Payable",    icon: Wallet,   color: "#059669" },
    { key: "surveil",    label: "En attente", icon: Clock,    color: "#D97706" },
    { key: "depodirect", label: "Dépôt",      icon: Landmark, color: "#4F46E5" },
    { key: "mega",       label: "Méga",       icon: Sparkles, color: "#7C3AED" },
];

// Options pour le MultiSelect Statuts (réutilise FLAG_FILTERS)
const STATUT_OPTIONS = FLAG_FILTERS.map(({ key, label, icon, color }) => ({
    value: key, label, icon, color,
}));

// Articles Méga — multi-sélection OU (union)
const ARTICLE_OPTIONS = [
    { value: "art486", label: "Art. 486" },
    { value: "art672", label: "Art. 672" },
    { value: "art684", label: "Art. 684" },
    { value: "LPJ85",  label: "85.4.1 LPJ" },
    { value: "CPC278", label: "278 C.P.C." },
];

// Districts — multi-sélection OU (union) — 36 entrées, recherchable
const DISTRICT_OPTIONS = QC_DISTRICTS.map((d) => ({ value: String(d.id), label: d.nom }));

const readInitialFiltersFromUrl = (search) => {
    const params = new URLSearchParams(search);
    const flags = new Set();
    FLAG_FILTERS.forEach(({ key }) => {
        if (params.get(key) === "true") flags.add(key);
    });
    const statut = ["actif", "inactif"].includes(params.get("statut") || "")
        ? params.get("statut") : "all";
    const typeAvocat = ["permanent", "prive"].includes(params.get("type_avocat") || "")
        ? params.get("type_avocat") : "all";
    return { flags, statut, typeAvocat };
};

export default function AvocatsList() {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "editeur" || user?.role === "ti";
    const location = useLocation();

    // Lecture des filtres initiaux UNE SEULE FOIS (au montage du composant).
    // useRef garantit que cette lecture ne se re-évalue pas même si location change.
    const initialFiltersRef = useRef(readInitialFiltersFromUrl(location.search));

    // Liste paginée en mode infinite scroll : chaque fetch ajoute à `items`.
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);          // chargement initial
    const [loadingMore, setLoadingMore] = useState(false); // chargement page suivante

    const [q, setQ] = useState("");
    const [statut, setStatut] = useState(initialFiltersRef.current.statut);
    const [typeAvocat, setTypeAvocat] = useState(initialFiltersRef.current.typeAvocat);
    // statutFlags : Set<"factweb"|"payable"|"surveil"|"depodirect"|"mega">
    //   ET-intersection. Initialisé depuis l'URL (cards Dashboard).
    const [statutFlags, setStatutFlags] = useState(initialFiltersRef.current.flags);
    // articleFlags : Set<"art486"|...> — OR-union, filtre les avocats Méga
    const [articleFlags, setArticleFlags] = useState(new Set());
    // districtFlags : Set<"1"|"2"|... ID stringifié> — OR-union sur InfoDistrict
    const [districtFlags, setDistrictFlags] = useState(new Set());
    // Tri de la grille : { key, dir } — dir ∈ {"asc","desc"}. Clic sur en-tête cycle :
    // asc → desc → asc. Colonnes triables : code, nom, prenom, ville, email, updated_at.
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    const toggleSort = (key) => {
        setSort((s) => {
            if (s.key !== key) return { key, dir: "asc" };
            return { key, dir: s.dir === "asc" ? "desc" : "asc" };
        });
    };

    // Les filtres Articles + Districts n'ont de sens que dans le contexte Méga.
    // On les affiche uniquement si "Méga" est coché dans les Statuts.
    const megaScopeActive = statutFlags.has("mega");

    // Quand Méga est désélectionné, on purge automatiquement les sélections
    // Articles + Districts (sinon le backend filtrerait sur des codes qui ne
    // peuvent matcher aucun avocat sans InfoMega/InfoDistrict, créant une
    // expérience confuse).
    useEffect(() => {
        if (!megaScopeActive) {
            if (articleFlags.size > 0) setArticleFlags(new Set());
            if (districtFlags.size > 0) setDistrictFlags(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [megaScopeActive]);
    // pendingCodes : null = filtre désactivé ; [] = activé mais aucun résultat ;
    // [codes...] = activé avec liste de codes filtrant la grille.
    const [pendingCodes, setPendingCodes] = useState(null);
    // missingPendingCodes : codes retournés par WebCenter mais absents de CardAvo
    // (le filtre `Avocat.code.in_(codes)` les masque silencieusement → on les
    // remonte à l'utilisateur pour expliquer l'écart entre badge et grille).
    const [missingPendingCodes, setMissingPendingCodes] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [editing, setEditing] = useState(null);

    // Ref interne pour l'observer + garde-fou anti-double-fetch
    const inFlightRef = useRef(false);

    const fetchPage = useCallback(async (targetPage, isReset) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        if (isReset) setLoading(true); else setLoadingMore(true);
        try {
            const params = { page: targetPage, page_size: PAGE_SIZE };
            if (q.trim()) params.q = q.trim();
            if (statut !== "all") params.statut = statut;
            if (typeAvocat !== "all") params.type_avocat = typeAvocat;
            // Ajoute chaque statut actif (mega, factweb, payable, surveil, depodirect)
            // au query — intersection ET côté backend.
            for (const k of statutFlags) params[k] = true;
            // Articles Méga (union OU)
            if (articleFlags.size > 0) params.articles = [...articleFlags].join(",");
            // Districts (union OU)
            if (districtFlags.size > 0) params.districts = [...districtFlags].join(",");
            // Tri (asc/desc) — n'envoie rien si aucun tri actif → défaut backend (nom, prénom)
            if (sort.key) {
                params.sort = sort.key;
                params.direction = sort.dir;
            }
            // Filtre additionnel par liste de codes (bouton « Lettres en attente »)
            if (pendingCodes !== null) {
                if (pendingCodes.length === 0) {
                    // Aucun code → on évite l'appel API et on retourne 0 résultat.
                    setItems([]);
                    setTotal(0);
                    setHasMore(false);
                    return;
                }
                params.codes = pendingCodes.join(",");
            }
            const { data } = await api.get("/avocats", { params });
            const newItems = data?.items || [];
            setItems((prev) => (isReset ? newItems : [...prev, ...newItems]));
            setTotal(data?.total || 0);
            // hasMore = il reste au moins une page après celle qu'on vient de recevoir
            const loadedSoFar = (isReset ? 0 : items.length) + newItems.length;
            setHasMore(loadedSoFar < (data?.total || 0) && newItems.length > 0);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Erreur de chargement");
            setHasMore(false);
        } finally {
            if (isReset) setLoading(false); else setLoadingMore(false);
            inFlightRef.current = false;
        }
    // items.length est utilisé pour calculer loadedSoFar — ok dans useCallback
    // car les fetchs suivants sont déclenchés via setPage() qui change la deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, statut, typeAvocat, pendingCodes, statutFlags, articleFlags, districtFlags, sort]);

    // Reset complet quand les filtres changent (avec debounce sur la recherche)
    useEffect(() => {
        const id = setTimeout(() => {
            setPage(1);
            fetchPage(1, true);
        }, 300);
        return () => clearTimeout(id);
    }, [q, statut, typeAvocat, pendingCodes, statutFlags, articleFlags, districtFlags, sort, fetchPage]);

    // Charge la page suivante quand `page` change (déclenché par le sentinel)
    useEffect(() => {
        if (page > 1) fetchPage(page, false);
    }, [page, fetchPage]);

    // Scroll infini : on attache un onScroll sur le conteneur de la table.
    // Quand le bas est proche, on charge la page suivante. Pattern simple,
    // sans IntersectionObserver, qui évite les contraintes React 19 sur
    // setState dans une callback ref.
    const handleScroll = useCallback((e) => {
        if (!hasMore || loadingMore || loading || inFlightRef.current) return;
        const el = e.currentTarget;
        // Déclenche le chargement quand il reste moins de 250px à scroller.
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
            setPage((p) => p + 1);
        }
    }, [hasMore, loadingMore, loading]);

    const refetchAll = useCallback(() => {
        setPage(1);
        fetchPage(1, true);
    }, [fetchPage]);

    const handleSaved = ({ close = false, updatedAvocat = null, silent = false } = {}) => {
        // En mode autosave (silent), on évite de recharger toute la liste à chaque
        // sauvegarde — on met juste à jour la ligne éditée si la response contient l'avocat.
        if (silent) {
            if (updatedAvocat) {
                setItems((prev) => prev.map((a) => a.id === updatedAvocat.id ? updatedAvocat : a));
                setEditing(updatedAvocat);
            }
            return;
        }
        refetchAll();
        if (close) setEditing(null);
        else if (updatedAvocat) setEditing(updatedAvocat);
    };

    // Bouton « Lettres en attente » : interroge WebCenter pour récupérer la
    // liste des codes AvoPP dont Date_Lettre IS NULL, puis filtre la grille
    // sur cette liste. Un nouveau clic après activation efface le filtre.
    const fetchPendingLetters = async () => {
        if (pendingCodes !== null) {
            // Filtre déjà actif → on l'éteint.
            setPendingCodes(null);
            setMissingPendingCodes([]);
            return;
        }
        setPendingLoading(true);
        try {
            const { data } = await api.get("/avocats/pending-letters");
            if (data?.error) {
                toast.error(`WebCenter : ${data.error}`);
                return;
            }
            const codes = data?.codes || [];
            const missing = data?.missing_codes || [];
            const found = data?.found_codes || [];
            setPendingCodes(codes);
            setMissingPendingCodes(missing);
            if (codes.length === 0) {
                toast.info("Aucune inscription en attente.");
            } else if (missing.length > 0) {
                const previewMissing = missing.slice(0, 5).join(", ");
                const suffix = missing.length > 5 ? "…" : "";
                toast.warning(
                    `${codes.length} inscription${codes.length > 1 ? "s" : ""} en attente sur WebCenter ` +
                    `— ${found.length} dans CardAvo, ${missing.length} code${missing.length > 1 ? "s" : ""} ` +
                    `introuvable${missing.length > 1 ? "s" : ""} : ${previewMissing}${suffix}`,
                    { duration: 8000 }
                );
            } else {
                toast.success(`${codes.length} inscription${codes.length > 1 ? "s" : ""} en attente.`);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de l'interrogation de WebCenter");
        } finally {
            setPendingLoading(false);
        }
    };

    return (
        <div className="h-full flex flex-col gap-3 -m-6 md:-m-8 p-6 md:p-8" data-testid="avocats-page">
            {/* En-tête compact (sans overline « Répertoire » pour gagner de l'espace) */}
            <div className="flex items-center justify-between gap-4 flex-wrap shrink-0">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                        Avocats
                        <span className="ml-3 text-sm font-normal text-slate-500">
                            {fmt(total)} {total > 1 ? "fiches" : "fiche"}
                        </span>
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant={pendingCodes !== null ? "default" : "outline"}
                        onClick={fetchPendingLetters}
                        disabled={pendingLoading}
                        className={`rounded-md h-9 ${
                            pendingCodes !== null
                                ? "bg-amber-500 hover:bg-amber-600 text-white"
                                : ""
                        }`}
                        title={
                            pendingCodes !== null && missingPendingCodes.length > 0
                                ? `${pendingCodes.length - missingPendingCodes.length} avocat${pendingCodes.length - missingPendingCodes.length > 1 ? "s" : ""} dans CardAvo · ${missingPendingCodes.length} code${missingPendingCodes.length > 1 ? "s" : ""} WebCenter introuvable${missingPendingCodes.length > 1 ? "s" : ""} : ${missingPendingCodes.join(", ")}`
                                : "Lister les avocats AvoPP dont l'inscription est en attente (lettre non envoyée — source : WebCenter)"
                        }
                        data-testid="pending-letters-btn"
                    >
                        {pendingLoading ? (
                            <Loader2 size={14} className="mr-1.5 animate-spin" />
                        ) : (
                            <MailWarning size={14} className="mr-1.5" />
                        )}
                        <span translate="no">Inscription en attente</span>
                        {pendingCodes !== null && (
                            <>
                                <span
                                    className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded bg-white/25 text-xs font-semibold"
                                    data-testid="pending-letters-badge"
                                >
                                    {missingPendingCodes.length > 0
                                        ? `${pendingCodes.length - missingPendingCodes.length}/${pendingCodes.length}`
                                        : pendingCodes.length}
                                </span>
                                <X size={12} className="ml-2" />
                            </>
                        )}
                    </Button>
                    {canEdit && (
                        <Button
                            onClick={() => setEditing({})}
                            className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white h-9"
                            data-testid="add-avocat-button"
                        >
                            <Plus size={14} className="mr-1.5" /> Nouvel avocat
                        </Button>
                    )}
                </div>
            </div>

            {/* Filtres compacts */}
            <div className="bg-white border border-slate-200 rounded-md p-3 shrink-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Recherche par code, nom, prénom — ex. « Tremblay Alain »"
                            className="pl-9 h-9 rounded-md"
                            data-testid="avocat-search-input"
                        />
                    </div>
                    <Select value={statut} onValueChange={setStatut}>
                        <SelectTrigger className="w-[160px] h-9 rounded-md" data-testid="avocat-status-filter">
                            <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            <SelectItem value="actif">Actifs</SelectItem>
                            <SelectItem value="inactif">Inactifs</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={typeAvocat} onValueChange={setTypeAvocat}>
                        <SelectTrigger className="w-[180px] h-9 rounded-md" data-testid="avocat-type-filter">
                            <SelectValue placeholder="Type d'avocat" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les types</SelectItem>
                            <SelectItem value="permanent">Permanent (A)</SelectItem>
                            <SelectItem value="prive">Pratique privée (P, N)</SelectItem>
                        </SelectContent>
                    </Select>
                    <MultiSelect
                        options={STATUT_OPTIONS}
                        selected={statutFlags}
                        onChange={setStatutFlags}
                        label="Statuts"
                        width="w-[170px]"
                        testid="filter-statuts"
                    />
                    {megaScopeActive && (
                        <>
                            <MultiSelect
                                options={ARTICLE_OPTIONS}
                                selected={articleFlags}
                                onChange={setArticleFlags}
                                label="Articles habilités"
                                width="w-[180px]"
                                testid="filter-articles"
                            />
                            <MultiSelect
                                options={DISTRICT_OPTIONS}
                                selected={districtFlags}
                                onChange={setDistrictFlags}
                                label="Districts habilités"
                                width="w-[190px]"
                                searchable
                                testid="filter-districts"
                            />
                        </>
                    )}
                    <Button
                        variant="outline"
                        onClick={async () => {
                            // Construit les query params comme fetchPage le fait
                            try {
                                const params = new URLSearchParams();
                                if (q.trim()) params.set("q", q.trim());
                                if (statut !== "all") params.set("statut", statut);
                                if (typeAvocat !== "all") params.set("type_avocat", typeAvocat);
                                for (const k of statutFlags) params.set(k, "true");
                                if (articleFlags.size > 0) params.set("articles", [...articleFlags].join(","));
                                if (districtFlags.size > 0) params.set("districts", [...districtFlags].join(","));
                                if (pendingCodes !== null && pendingCodes.length > 0) params.set("codes", pendingCodes.join(","));
                                // axios -> blob pour ouvrir le PDF dans un nouvel onglet
                                const { data: blob } = await api.get(`/avocats/export-pdf?${params.toString()}`, {
                                    responseType: "blob",
                                });
                                const url = URL.createObjectURL(blob);
                                window.open(url, "_blank", "noopener,noreferrer");
                                // libère l'URL après quelques secondes (le PDF reste chargé dans l'onglet)
                                setTimeout(() => URL.revokeObjectURL(url), 30000);
                            } catch (e) {
                                toast.error(formatApiError(e.response?.data?.detail) || "Erreur génération PDF");
                            }
                        }}
                        className="h-9 rounded-md ml-auto"
                        data-testid="btn-print-list"
                        title="Générer un PDF de la liste actuellement filtrée"
                    >
                        <Printer size={14} className="mr-1.5" />
                        Imprimer la liste
                    </Button>
                </div>
            </div>

            {/* Table avec scroll infini */}
            <div
                className="bg-white border border-slate-200 rounded-md overflow-auto flex-1 min-h-0"
                onScroll={handleScroll}
            >
                <Table data-testid="avocats-table">
                    <TableHeader className="sticky top-0 bg-slate-50 z-10">
                        <TableRow className="bg-slate-50 [&>th]:h-8 [&>th]:py-0 [&>th]:text-xs">
                            <SortHead sortKey="code" label="Code" width="w-[90px]" sort={sort} onSort={toggleSort} />
                            <SortHead sortKey="nom" label="Nom" sort={sort} onSort={toggleSort} />
                            <SortHead sortKey="prenom" label="Prénom" sort={sort} onSort={toggleSort} />
                            <SortHead sortKey="ville" label="Ville" sort={sort} onSort={toggleSort} />
                            <SortHead sortKey="email" label="Courriel" sort={sort} onSort={toggleSort} />
                            <TableHead>Statut</TableHead>
                            {megaScopeActive && (
                                <>
                                    <TableHead data-testid="th-mega-articles">Articles</TableHead>
                                    <TableHead data-testid="th-mega-districts">Districts</TableHead>
                                </>
                            )}
                            <SortHead sortKey="updated_at" label="Date modif" width="w-[110px]" sort={sort} onSort={toggleSort} />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && items.length === 0 ? (
                            Array.from({ length: 12 }).map((_, i) => (
                                <TableRow key={`sk-${i}`} className="[&>td]:py-1">
                                    {Array.from({ length: megaScopeActive ? 9 : 7 }).map((_, j) => (
                                        <TableCell key={j}>
                                            <div className="h-3.5 bg-slate-100 rounded animate-pulse" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={megaScopeActive ? 9 : 7} className="text-center py-12 text-slate-500">
                                    Aucun avocat trouvé.
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {items.map((a) => (
                                    <TableRow
                                        key={a.id}
                                        className="hover:bg-slate-50 cursor-pointer [&>td]:py-1 [&>td]:text-sm"
                                        onClick={() => setEditing(a)}
                                        data-testid={`avocat-row-${a.code}`}
                                    >
                                        <TableCell className="font-mono text-xs text-slate-700">
                                            {a.code}
                                        </TableCell>
                                        <TableCell className="font-medium text-slate-900">{a.nom}</TableCell>
                                        <TableCell>{a.prenom}</TableCell>
                                        <TableCell className="text-slate-600">{a.adresse?.ville || "—"}</TableCell>
                                        <TableCell className="text-slate-600 truncate max-w-[260px]" title={a.adresse?.email || ""}>
                                            {a.adresse?.email || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {a.actif ? (
                                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 rounded-md font-medium px-1.5 py-0 text-[11px]">
                                                        Actif
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 rounded-md font-medium px-1.5 py-0 text-[11px]">
                                                        Inactif
                                                    </Badge>
                                                )}
                                                {a.factweb && (
                                                    <Badge
                                                        title="Facturation web activée"
                                                        data-testid={`badge-web-${a.id}`}
                                                        className="bg-sky-100 text-sky-700 hover:bg-sky-100 rounded-md font-medium px-1.5 py-0 text-[11px]"
                                                    >
                                                        Web
                                                    </Badge>
                                                )}
                                                {a.payable && (
                                                    <Badge
                                                        title="Avocat payable"
                                                        data-testid={`badge-payable-${a.id}`}
                                                        className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-md font-medium px-1.5 py-0 text-[11px]"
                                                    >
                                                        Payable
                                                    </Badge>
                                                )}
                                                {a.surveil && (
                                                    <Badge
                                                        title="En attente"
                                                        data-testid={`badge-attente-${a.id}`}
                                                        className="bg-amber-100 text-amber-700 hover:bg-amber-100 rounded-md font-medium px-1.5 py-0 text-[11px]"
                                                    >
                                                        En attente
                                                    </Badge>
                                                )}
                                                {a.depodirect && (
                                                    <Badge
                                                        title="Dépôt direct activé"
                                                        data-testid={`badge-depot-${a.id}`}
                                                        className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 rounded-md font-medium px-1.5 py-0 text-[11px]"
                                                    >
                                                        Dépôt
                                                    </Badge>
                                                )}
                                                {a.mega && (
                                                    <Badge
                                                        title="Avocat inscrit au programme Méga"
                                                        data-testid={`badge-mega-${a.id}`}
                                                        className="bg-violet-100 text-violet-700 hover:bg-violet-100 rounded-md font-medium px-1.5 py-0 text-[11px]"
                                                    >
                                                        Méga
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        {megaScopeActive && (
                                            <>
                                                <TableCell
                                                    className="text-slate-700 text-xs"
                                                    data-testid={`cell-articles-${a.id}`}
                                                    title={(a.mega_articles || []).join(", ")}
                                                >
                                                    {(a.mega_articles && a.mega_articles.length > 0)
                                                        ? a.mega_articles.map((code) => {
                                                            const lbl = {
                                                                art486: "486", art672: "672", art684: "684",
                                                                LPJ85: "85.4.1", CPC278: "278",
                                                            }[code] || code;
                                                            return (
                                                                <span key={code}
                                                                    className="inline-block mr-1 px-1 rounded bg-slate-100 text-slate-700 text-[10px] font-medium">
                                                                    {lbl}
                                                                </span>
                                                            );
                                                        })
                                                        : <span className="text-slate-400">—</span>}
                                                </TableCell>
                                                <TableCell
                                                    className="text-slate-700 text-xs"
                                                    data-testid={`cell-districts-${a.id}`}
                                                >
                                                    {(a.mega_districts && a.mega_districts.length > 0)
                                                        ? a.mega_districts.join(", ")
                                                        : <span className="text-slate-400">—</span>}
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell className="text-slate-500 text-xs font-mono whitespace-nowrap">
                                            {formatDateShort(a.updated_at)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* Ligne sentinelle : invisible, déclenche le chargement de la suite
                                    quand elle entre dans le viewport (IntersectionObserver). */}
                                {hasMore && (
                                    <TableRow data-testid="infinite-scroll-sentinel">
                                        <TableCell colSpan={megaScopeActive ? 9 : 7} className="text-center py-2 text-xs text-slate-400">
                                            {loadingMore ? (
                                                <span className="inline-flex items-center gap-2">
                                                    <Loader2 size={12} className="animate-spin" />
                                                    Chargement…
                                                </span>
                                            ) : (
                                                "\u00A0"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!hasMore && items.length >= PAGE_SIZE && (
                                    <TableRow>
                                        <TableCell colSpan={megaScopeActive ? 9 : 7} className="text-center py-3 text-xs text-slate-400">
                                            Fin de la liste — {fmt(items.length)} fiche{items.length > 1 ? "s" : ""} affichée{items.length > 1 ? "s" : ""}.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </div>

            <AvocatSheet
                open={editing !== null}
                onOpenChange={(open) => !open && setEditing(null)}
                avocat={editing}
                onSaved={handleSaved}
            />
        </div>
    );
}

// En-tête de colonne triable. Cycle asc→desc→asc au clic ; affiche l'icône
// correspondante à droite du libellé.
function SortHead({ sortKey, label, width, sort, onSort }) {
    const isActive = sort.key === sortKey;
    const Icon = !isActive ? ArrowUpDown : (sort.dir === "asc" ? ArrowUp : ArrowDown);
    return (
        <TableHead className={width}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={`flex items-center gap-1 select-none hover:text-slate-900 transition-colors ${
                    isActive ? "text-slate-900 font-semibold" : "text-slate-600"
                }`}
                data-testid={`sort-${sortKey}`}
                title={`Trier par ${label}`}
            >
                {label}
                <Icon
                    size={12}
                    className={isActive ? "text-[#0033A0]" : "text-slate-400"}
                    strokeWidth={2.5}
                />
            </button>
        </TableHead>
    );
}
